import React, { createContext, useState, useContext, useEffect } from 'react';
import { loginUser, logoutUser, registerUser, verifyUserToken, passwordResetRequest } from '../api/authApi';
import { useToast } from '../components/ToastProvider/ToastProvider';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }

    const handleAuthExpired = () => {
      setUser(null);
      showToast('Sharepoint access expired. Kindly log in again.', 'warning', 10000);
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [showToast]);

  const login = async (email, password) => {
    try {
      const data = await loginUser(email, password);
      localStorage.setItem('token', data.token);
      setUser(data.data);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await logoutUser(token);
        if (!response.ok) {
          console.warn('Logout request failed, clearing local session anyway');
        }
      }
    } catch (error) {
      console.warn('Error during logout:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const register = async (name, email, password) => {
    try {
      const data = await registerUser(name, email, password);
      localStorage.setItem('token', data.token);
      setUser(data.user);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  const verifyToken = async (token) => {
    try {
      const data = await verifyUserToken(token);
      setUser(data.data);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Token verification error:', error);
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
      return false;
    }
  };

  const loginWithToken = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    return true;
  };

  const passwordReset = async (email) => {
    try {
      await passwordResetRequest(email);
    } catch (error) {
      console.error('Password reset request error:', error);
      
      return false;
    }

    return true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      register, 
      loading,
      loginWithToken,
      passwordReset
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 